/**
 * TagsCell Component
 *
 * Renders multiple tags with add/remove capability.
 * Supports autocomplete suggestions from existing tags.
 */

import { useState, useRef, useEffect, Fragment } from 'react';
import { Transition } from '@headlessui/react';
import { XMarkIcon, PlusIcon, TagIcon } from '@heroicons/react/24/outline';
import type { TagsCellProps } from './types';

// Tag color palette for visual variety
const TAG_COLORS = [
  { bg: '#e8f5e9', text: '#2e7d32' }, // Green
  { bg: '#e3f2fd', text: '#1565c0' }, // Blue
  { bg: '#fff3e0', text: '#ef6c00' }, // Orange
  { bg: '#f3e5f5', text: '#7b1fa2' }, // Purple
  { bg: '#fce4ec', text: '#c2185b' }, // Pink
  { bg: '#e0f7fa', text: '#00838f' }, // Cyan
  { bg: '#fff8e1', text: '#ff8f00' }, // Amber
  { bg: '#efebe9', text: '#5d4037' }, // Brown
];

// Get consistent color for a tag based on its name
const getTagColor = (tag: string): { bg: string; text: string } => {
  let hash = 0;
  for (let i = 0; i < tag.length; i++) {
    hash = tag.charCodeAt(i) + ((hash << 5) - hash);
  }
  const index = Math.abs(hash) % TAG_COLORS.length;
  return TAG_COLORS[index] ?? { bg: '#e8f5e9', text: '#2e7d32' };
};

export function TagsCell({
  value,
  onChange,
  suggestions = [],
  maxTags = 50,
  readonly = false,
}: TagsCellProps) {
  const [isOpen, setIsOpen] = useState(false);
  const [inputValue, setInputValue] = useState('');
  const inputRef = useRef<HTMLInputElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);

  const tags = value?.tags ?? [];

  // Filter suggestions based on input and exclude already selected tags
  const filteredSuggestions = inputValue === ''
    ? suggestions.filter(s => !tags.includes(s))
    : suggestions.filter(s =>
        s.toLowerCase().includes(inputValue.toLowerCase()) &&
        !tags.includes(s)
      );

  // Close on click outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [isOpen]);

  const handleAddTag = (tag: string) => {
    if (readonly || !onChange) return;

    const trimmed = tag.trim();
    if (!trimmed || tags.includes(trimmed) || tags.length >= maxTags) return;

    onChange({ tags: [...tags, trimmed] });
    setInputValue('');
  };

  const handleRemoveTag = (tag: string) => {
    if (readonly || !onChange) return;

    const newTags = tags.filter(t => t !== tag);
    onChange(newTags.length > 0 ? { tags: newTags } : null);
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      if (inputValue.trim()) {
        handleAddTag(inputValue);
      }
    }
    if (e.key === 'Backspace' && !inputValue && tags.length > 0) {
      // Remove last tag on backspace when input is empty
      const lastTag = tags[tags.length - 1];
      if (lastTag) {
        handleRemoveTag(lastTag);
      }
    }
    if (e.key === 'Escape') {
      setIsOpen(false);
    }
  };

  if (readonly) {
    return (
      <div className="w-full h-full flex items-center overflow-hidden">
        {tags.length > 0 ? (
          <div className="flex gap-1 overflow-hidden">
            {tags.slice(0, 3).map((tag) => {
              const color = getTagColor(tag);
              return (
                <span
                  key={tag}
                  className="px-2 py-0.5 text-xs font-medium rounded"
                  style={{ backgroundColor: color.bg, color: color.text }}
                >
                  {tag}
                </span>
              );
            })}
            {tags.length > 3 && (
              <span className="text-xs text-gray-500">+{tags.length - 3}</span>
            )}
          </div>
        ) : (
          <span className="text-gray-400 text-sm">-</span>
        )}
      </div>
    );
  }

  return (
    <div ref={containerRef} className="relative w-full">
      <div
        onClick={() => setIsOpen(true)}
        className="w-full h-full flex items-center cursor-text min-h-[28px] overflow-hidden"
      >
        {tags.length > 0 ? (
          <div className="flex gap-1 overflow-hidden">
            {tags.slice(0, 3).map((tag) => {
              const color = getTagColor(tag);
              return (
                <span
                  key={tag}
                  className="px-2 py-0.5 text-xs font-medium rounded"
                  style={{ backgroundColor: color.bg, color: color.text }}
                >
                  {tag}
                </span>
              );
            })}
            {tags.length > 3 && (
              <span className="text-xs text-gray-500">+{tags.length - 3}</span>
            )}
          </div>
        ) : (
          <span className="text-gray-400 text-sm hover:text-gray-500">-</span>
        )}
      </div>

      <Transition
        show={isOpen}
        as={Fragment}
        leave="transition ease-in duration-100"
        leaveFrom="opacity-100"
        leaveTo="opacity-0"
      >
        <div className="absolute z-50 mt-1 bg-white rounded-lg shadow-dropdown border border-surface-border w-72 overflow-hidden">
          {/* Current tags */}
          <div className="p-3 border-b border-surface-border">
            <div className="flex flex-wrap gap-1 mb-2">
              {tags.map((tag) => {
                const color = getTagColor(tag);
                return (
                  <span
                    key={tag}
                    className="inline-flex items-center gap-1 px-2 py-0.5 text-xs font-medium rounded"
                    style={{ backgroundColor: color.bg, color: color.text }}
                  >
                    {tag}
                    <button
                      onClick={() => handleRemoveTag(tag)}
                      className="hover:opacity-70"
                    >
                      <XMarkIcon className="h-3 w-3" />
                    </button>
                  </span>
                );
              })}
              {tags.length === 0 && (
                <span className="text-xs text-gray-400">No tags yet</span>
              )}
            </div>

            {/* Tag input */}
            <div className="flex items-center gap-2">
              <TagIcon className="h-4 w-4 text-gray-400" />
              <input
                ref={inputRef}
                type="text"
                value={inputValue}
                onChange={(e) => setInputValue(e.target.value)}
                onKeyDown={handleKeyDown}
                placeholder={tags.length >= maxTags ? 'Max tags reached' : 'Add tag...'}
                disabled={tags.length >= maxTags}
                className="flex-1 text-sm bg-transparent border-none outline-none placeholder-gray-400"
                autoFocus
              />
              {inputValue && (
                <button
                  onClick={() => handleAddTag(inputValue)}
                  className="text-primary-600 hover:text-primary-700"
                >
                  <PlusIcon className="h-4 w-4" />
                </button>
              )}
            </div>
          </div>

          {/* Suggestions */}
          {filteredSuggestions.length > 0 && (
            <div className="max-h-36 overflow-auto">
              <div className="px-3 py-1.5 text-xs font-medium text-gray-500">
                Suggestions
              </div>
              {filteredSuggestions.slice(0, 10).map((suggestion) => {
                const color = getTagColor(suggestion);
                return (
                  <button
                    key={suggestion}
                    onClick={() => handleAddTag(suggestion)}
                    className="w-full px-3 py-2 text-left hover:bg-surface-hover flex items-center gap-2"
                  >
                    <span
                      className="px-2 py-0.5 text-xs font-medium rounded"
                      style={{ backgroundColor: color.bg, color: color.text }}
                    >
                      {suggestion}
                    </span>
                  </button>
                );
              })}
            </div>
          )}

          {/* Done button */}
          <div className="border-t border-surface-border p-2">
            <button
              onClick={() => setIsOpen(false)}
              className="w-full px-3 py-1.5 text-sm text-gray-600 hover:bg-surface-hover rounded"
            >
              Done
            </button>
          </div>
        </div>
      </Transition>
    </div>
  );
}
